// React, Next
import { ReactElement, useContext } from 'react'
import { useTranslation } from 'next-i18next'

// MUI
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'

// Icons
import TipsAndUpdatesIcon from '@mui/icons-material/TipsAndUpdates'
import SavingsIcon from '@mui/icons-material/Savings'
import HistoryToggleOffIcon from '@mui/icons-material/HistoryToggleOff'
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser'

// Custom
import Section from '@/components/sections/Section'
import SectionHeader from '@/components/sections/SectionHeader'
import BulletGrid from '@/components/sections/BulletGrid'
import { GlobalContext } from 'context/GlobalContextProvider'

// Style
import themeColors from 'assets/theme/colors'
import breakpoints from 'assets/theme/breakpoints'
import { getFontBodySmall } from '@/assets/theme/fonts'

export default function WhyRetrofitSection() {
  const { t } = useTranslation('servicesTestEquipmentModernization')
  const { screenDimensions } = useContext(GlobalContext)

  const stacked = screenDimensions.width < breakpoints.desktopToMobile

  const items = [
    t('why.items.01'),
    t('why.items.02'),
    t('why.items.03'),
    t('why.items.04'),
    t('why.items.05'),
    t('why.items.06'),
    t('why.items.07'),
    t('why.items.08'),
    t('why.items.09'),
    t('why.items.10')
  ]

  const points: { icon: ReactElement; label: string }[] = [
    {
      icon: <SavingsIcon />,
      label: t('why.card.points.01')
    },
    {
      icon: <HistoryToggleOffIcon />,
      label: t('why.card.points.02')
    },
    {
      icon: <VerifiedUserIcon />,
      label: t('why.card.points.03')
    }
  ]

  /**
   * The commercial argument, pulled out of the list and addressed to whoever
   * signs off the budget rather than to whoever runs the machine.
   */
  const getCard = () => (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        background: themeColors.kroemtech.background,
        border: `1px solid ${themeColors.gray.lighter}`,
        borderRadius: '14px',
        padding: stacked ? '2rem 1.5rem' : '2.5rem 2rem',
        boxSizing: 'border-box'
      }}
    >
      <Box
        sx={{
          alignSelf: 'center',
          width: '4rem',
          height: '4rem',
          borderRadius: '50%',
          background: `${themeColors.success.main}1F`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: '1.4rem'
        }}
      >
        <TipsAndUpdatesIcon
          sx={{ fontSize: '2rem', color: themeColors.success.darker }}
        />
      </Box>

      <Typography
        component="p"
        sx={{
          textAlign: 'center',
          fontSize: '0.8rem',
          fontWeight: 700,
          letterSpacing: '0.09em',
          textTransform: 'uppercase',
          color: themeColors.success.darker,
          marginBottom: '0.6rem'
        }}
      >
        {t('why.card.eyebrow')}
      </Typography>

      <Typography
        variant="h3"
        component="h3"
        sx={{
          textAlign: 'center',
          fontSize: stacked ? '1.35rem' : '1.5rem',
          fontWeight: 700,
          lineHeight: 1.25,
          color: themeColors.kroemtech.dunkelblau,
          marginBottom: '0.9rem'
        }}
      >
        {t('why.card.title')}
      </Typography>

      <Typography
        component="p"
        sx={{
          textAlign: 'center',
          fontSize: getFontBodySmall(),
          lineHeight: 1.65,
          color: themeColors.gray.defaultText
        }}
      >
        {t('why.card.description')}
      </Typography>

      <Box sx={{ marginTop: '1.75rem' }}>
        {points.map(point => (
          <Box
            key={point.label}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '1rem',
              padding: '0.9rem 0',
              borderTop: `1px solid ${themeColors.gray.lighter}`
            }}
          >
            <Box
              sx={{
                flexShrink: 0,
                width: '2.5rem',
                height: '2.5rem',
                borderRadius: '50%',
                border: `1px solid ${themeColors.success.main}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: themeColors.success.darker,
                '& svg': { fontSize: '1.25rem' }
              }}
            >
              {point.icon}
            </Box>

            <Typography
              component="span"
              sx={{
                fontSize: getFontBodySmall(),
                lineHeight: 1.45,
                color: themeColors.kroemtech.dunkelblau
              }}
            >
              {point.label}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  )

  /**
   * The card runs alongside the header rather than under it, so its top edge
   * starts level with the section and fills the space the intro leaves free.
   */
  const getContent = () => (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: stacked ? '1fr' : '1fr minmax(0, 25rem)',
        gap: stacked ? '2.5rem' : '3rem',
        alignItems: 'start',
        width: '100%'
      }}
    >
      <Box>
        <SectionHeader
          eyebrow={t('why.eyebrow')}
          title={t('why.title')}
          intro={[t('why.intro1'), t('why.intro2'), t('why.intro3')]}
        />

        <BulletGrid items={items} columns={stacked ? 2 : 1} />
      </Box>

      {getCard()}
    </Box>
  )

  // *** MAIN RENDER ***
  return (
    <Section
      background={themeColors.section.light}
      paddingTop="5rem"
      paddingBottom="5rem"
      content={getContent()}
    />
  )
}
